import "../../global.css";
import { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { userRequest } from "../../requestMethods";
import { useCompany } from "../../components/context/CompanyContext";
import { titleSetRef } from "../../redux/titleRedux";
import CustomGrid from "../../components/grid/CustomGrid";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import SendIcon from "@mui/icons-material/Send";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LanguageIcon from "@mui/icons-material/Language";
import Tooltip from "@mui/material/Tooltip";
import Swal from "sweetalert2";

const FILTER_ALL = "all";
const FILTER_PUSH_ON = "push_on";
const FILTER_PUSH_OFF = "push_off";
const FILTER_INSTALLED = "installed";
const FILTER_BROWSER = "browser";

const filterOptions = [
  { value: FILTER_ALL, label: "Todos" },
  { value: FILTER_PUSH_ON, label: "Sino Ativo", icon: <NotificationsActiveIcon style={{ fontSize: 14 }} /> },
  { value: FILTER_PUSH_OFF, label: "Sino Inativo", icon: <NotificationsOffIcon style={{ fontSize: 14 }} /> },
  { value: FILTER_INSTALLED, label: "Instalado", icon: <PhoneAndroidIcon style={{ fontSize: 14 }} /> },
  { value: FILTER_BROWSER, label: "Browser", icon: <LanguageIcon style={{ fontSize: 14 }} /> },
];

function applySubFilter(list, subscriberUsers, filter, idField) {
  if (filter === FILTER_ALL) return list;
  return list.filter((u) => {
    const id = u[idField];
    const s = subscriberUsers.find((x) => x.id === id);
    if (filter === FILTER_PUSH_ON) return s?.pushEnabled === true;
    if (filter === FILTER_PUSH_OFF) return !s?.pushEnabled;
    if (filter === FILTER_INSTALLED) return s?.standalone === true;
    if (filter === FILTER_BROWSER) return s?.standalone === false;
    return true;
  });
}

function SubFilterBar({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
      {filterOptions.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            display: "flex", alignItems: "center", gap: 4,
            padding: "3px 10px", fontSize: 12, borderRadius: 14, cursor: "pointer",
            border: value === opt.value ? "1.5px solid #e87722" : "1.5px solid #ddd",
            background: value === opt.value ? "#fff3ec" : "#fafafa",
            color: value === opt.value ? "#e87722" : "#555",
            fontWeight: value === opt.value ? 600 : 400,
          }}
        >
          {opt.icon}{opt.label}
        </button>
      ))}
    </div>
  );
}

export default function PushNotificationList() {
  const { companyId } = useCompany();
  const dispatch = useDispatch();

  const [tab, setTab] = useState("users");
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [subscriberUsers, setSubscriberUsers] = useState([]);
  const [subscriberCustomers, setSubscriberCustomers] = useState([]);

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [searchRecipient, setSearchRecipient] = useState("");
  const [subFilter, setSubFilter] = useState(FILTER_ALL);

  useEffect(() => {
    dispatch(titleSetRef({ title: "Notificações Push" }));
    if (companyId > 0) {
      loadMessages();
      loadUsers();
      loadCustomers();
      loadSubscribers();
    }
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // reset filter/search on tab change
  useEffect(() => {
    setSearchRecipient("");
    setSubFilter(FILTER_ALL);
  }, [tab]);

  const loadMessages = async () => {
    try {
      const { data } = await userRequest.get(`${companyId}/push/messages`);
      setMessages(data);
    } catch {}
  };

  const loadSubscribers = async () => {
    try {
      const { data } = await userRequest.get(`${companyId}/push/subscribers`);
      setSubscriberUsers(data.users ?? []);
      setSubscriberCustomers(data.customers ?? []);
    } catch {}
  };

  const loadUsers = async () => {
    try {
      const { data } = await userRequest.get(`${companyId}/users`);
      setUsers(data ?? []);
    } catch {}
  };

  const loadCustomers = async () => {
    try {
      const { data } = await userRequest.get(`${companyId}/customers`, { params: { page: 1, limit: 25000 } });
      setCustomers(Array.isArray(data) ? data : (data?.rows ?? data?.customers ?? []));
    } catch (e) {
      console.error("[customers] error:", e);
    }
  };

  // ── VENDORS ──
  const subOrderUser = (u) => {
    const s = subscriberUsers.find((x) => x.id === u.userId);
    if (!s) return 4;
    if (s.pushEnabled && s.standalone === true) return 1;
    if (s.pushEnabled) return 2;
    if (s.standalone === true) return 3;
    return 4;
  };

  const filteredUsers = useMemo(() => {
    const q = searchRecipient.toLowerCase();
    const base = users.filter(
      (u) =>
        !q ||
        (u.name && u.name.toLowerCase().includes(q)) ||
        (u.username && String(u.username).toLowerCase().includes(q)),
    );
    return applySubFilter(base, subscriberUsers, subFilter, "userId").sort(
      (a, b) =>
        subOrderUser(a) - subOrderUser(b) ||
        (a.name || a.username || "").localeCompare(b.name || b.username || "", "pt-BR"),
    );
  }, [users, searchRecipient, subscriberUsers, subFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const allUsersSelected =
    filteredUsers.length > 0 && filteredUsers.every((r) => selectedUsers.includes(r.userId));

  const toggleUser = (id) =>
    setSelectedUsers((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const selectAllUsers = () => {
    const allIds = filteredUsers.map((u) => u.userId);
    const allSelected = allIds.every((id) => selectedUsers.includes(id));
    setSelectedUsers(allSelected ? [] : allIds);
  };

  // ── CUSTOMERS ──
  const subOrderCustomer = (c) => {
    const s = subscriberCustomers.find((x) => x.id === c.customerId);
    if (!s) return 4;
    if (s.pushEnabled && s.standalone === true) return 1;
    if (s.pushEnabled) return 2;
    if (s.standalone === true) return 3;
    return 4;
  };

  const filteredCustomers = useMemo(() => {
    const q = searchRecipient.toLowerCase();
    const base = customers.filter(
      (c) =>
        !q ||
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.contact && String(c.contact).toLowerCase().includes(q)),
    );
    return applySubFilter(base, subscriberCustomers, subFilter, "customerId").sort(
      (a, b) =>
        subOrderCustomer(a) - subOrderCustomer(b) ||
        (a.name || "").localeCompare(b.name || "", "pt-BR"),
    );
  }, [customers, searchRecipient, subscriberCustomers, subFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const allCustomersSelected =
    filteredCustomers.length > 0 && filteredCustomers.every((r) => selectedCustomers.includes(r.customerId));

  const toggleCustomer = (id) =>
    setSelectedCustomers((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const selectAllCustomers = () => {
    const allIds = filteredCustomers.map((c) => c.customerId);
    const allSelected = allIds.every((id) => selectedCustomers.includes(id));
    setSelectedCustomers(allSelected ? [] : allIds);
  };

  // ── SEND ──
  const handleSend = async () => {
    if (!title.trim()) {
      Swal.fire("Atenção!", "Informe o título da notificação.", "warning");
      return;
    }
    const isUsers = tab === "users";
    const selected = isUsers ? selectedUsers : selectedCustomers;
    if (selected.length === 0) {
      Swal.fire("Atenção!", `Selecione ao menos um ${isUsers ? "vendedor" : "cliente"}.`, "warning");
      return;
    }

    setSending(true);
    try {
      const payload = { title, body, url: url || undefined };
      const endpoint = isUsers
        ? (id) => `${companyId}/push/send/user/${id}`
        : (id) => `${companyId}/push/send/customer/${id}`;
      await Promise.all(selected.map((id) => userRequest.post(endpoint(id), payload)));
      Swal.fire("Enviado!", `Notificação enviada para ${selected.length} ${isUsers ? "vendedor(es)" : "cliente(s)"}.`, "success");
      setTitle("");
      setBody("");
      setUrl("");
      isUsers ? setSelectedUsers([]) : setSelectedCustomers([]);
      loadMessages();
    } catch {
      Swal.fire("Erro!", "Falha ao enviar notificação.", "error");
    } finally {
      setSending(false);
    }
  };

  // ── HISTORY ──
  const filteredMessages = useMemo(
    () =>
      messages
        .filter((m) => (tab === "users" ? m.userId != null : m.customerId != null))
        .map((m) => ({
          ...m,
          recipientName:
            tab === "users"
              ? (m.user?.name ?? m.user?.username ?? "-")
              : (m.customer?.name ?? "-"),
          recipientContact:
            tab === "users"
              ? (m.user?.contact ?? "-")
              : (m.customer?.contact ?? "-"),
        })),
    [messages, tab],
  );

  const historyColumns = [
    { field: "id", type: "number", headerName: "ID", width: 70 },
    { field: "recipientName", type: "text", headerName: "Nome", width: 160 },
    { field: "recipientContact", type: "text", headerName: "Telefone", width: 140 },
    { field: "title", type: "text", headerName: "Título", width: 180 },
    { field: "body", type: "text", headerName: "Mensagem", width: 240 },
    { field: "url", type: "text", headerName: "URL", width: 160 },
    { field: "readAt", type: "date", headerName: "Lida em", width: 150, format: "dd/MM/yyyy HH:mm" },
    { field: "createdAt", type: "date", headerName: "Enviada em", width: 150, format: "dd/MM/yyyy HH:mm" },
  ];

  // ── STATS ──
  const subList = tab === "users" ? subscriberUsers : subscriberCustomers;
  const totalList = tab === "users" ? users.length : customers.length;
  const activeCount = subList.filter((s) => s.pushEnabled).length;
  const installedCount = subList.filter((s) => s.standalone === true).length;
  const browserCount = subList.filter((s) => s.standalone === false).length;

  const selected = tab === "users" ? selectedUsers : selectedCustomers;
  const recipientLabel = tab === "users" ? "vendedor(es)" : "cliente(s)";

  // ── RECIPIENT LIST ──
  const renderRecipientList = () => {
    const isUsers = tab === "users";
    const list = isUsers ? filteredUsers : filteredCustomers;
    const subData = isUsers ? subscriberUsers : subscriberCustomers;
    const getId = (r) => (isUsers ? r.userId : r.customerId);
    const getLabel = (r) =>
      isUsers
        ? `${r.name || r.username || "—"} ${r.contact ? `· ${r.contact}` : ""} (${r.position || "vendedor"})`
        : `${r.name || "—"} ${r.contact ? `· ${r.contact}` : ""}`;
    const allSelected = isUsers ? allUsersSelected : allCustomersSelected;
    const selectedList = isUsers ? selectedUsers : selectedCustomers;
    const toggleFn = isUsers ? toggleUser : toggleCustomer;
    const selectAllFn = isUsers ? selectAllUsers : selectAllCustomers;

    return (
      <div style={{ border: "1px solid #ddd", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderBottom: "1px solid #eee", background: "#fafafa" }}>
          <input
            className="cust-input"
            style={{ flex: 1, height: 30, fontSize: 12 }}
            placeholder="Buscar..."
            value={searchRecipient}
            onChange={(e) => setSearchRecipient(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={selectedList.length > 0 && !allSelected}
                onChange={selectAllFn}
              />
            }
            label={<span style={{ fontSize: 12 }}>Todos ({list.length})</span>}
            style={{ margin: 0 }}
          />
        </div>

        <div style={{ maxHeight: 220, overflowY: "auto" }}>
          {list.length === 0 ? (
            <div style={{ padding: "12px 16px", fontSize: 13, color: "#888" }}>Nenhum encontrado.</div>
          ) : (
            <>
              {list.slice(0, 100).map((r) => {
                const id = getId(r);
                const subInfo = subData.find((s) => s.id === id);
                const isSubscribed = subInfo?.pushEnabled === true;
                return (
                  <div
                    key={id}
                    style={{
                      display: "flex", alignItems: "center", padding: "4px 10px",
                      borderBottom: "1px solid #f3f3f3",
                      background: selectedList.includes(id) ? "#fff8f5" : "white",
                      cursor: "pointer",
                    }}
                    onClick={() => toggleFn(id)}
                  >
                    <Checkbox size="small" checked={selectedList.includes(id)} onChange={() => {}} style={{ padding: "2px 6px 2px 0" }} />
                    {isSubscribed ? (
                      <NotificationsActiveIcon style={{ fontSize: 15, color: "#4caf50", marginRight: 2 }} />
                    ) : (
                      <NotificationsOffIcon style={{ fontSize: 15, color: "#ccc", marginRight: 2 }} />
                    )}
                    {subInfo?.standalone === true && (
                      <Tooltip title={[subInfo.deviceBrand, subInfo.deviceModel, subInfo.devicePlatform].filter(Boolean).join(" · ") || "Instalado"} arrow>
                        <PhoneAndroidIcon style={{ fontSize: 13, color: "#f4b400", marginRight: 6 }} />
                      </Tooltip>
                    )}
                    {subInfo?.standalone === false && (
                      <Tooltip title={[subInfo.deviceBrand, subInfo.deviceModel, subInfo.devicePlatform].filter(Boolean).join(" · ") || "Browser"} arrow>
                        <LanguageIcon style={{ fontSize: 13, color: "#2196f3", marginRight: 6 }} />
                      </Tooltip>
                    )}
                    <span style={{ fontSize: 13, color: "#333" }}>{getLabel(r)}</span>
                  </div>
                );
              })}
              {list.length > 100 && (
                <div style={{ padding: "6px 16px", fontSize: 12, color: "#888", background: "#fafafa" }}>
                  +{list.length - 100} não exibidos — use a busca para filtrar.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="cust-page">
      <div className="cust-title">Notificações Push</div>

      {/* ── TABS ── */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "2px solid #eee" }}>
        {[{ value: "users", label: "Vendedores" }, { value: "customers", label: "Clientes" }].map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            style={{
              padding: "8px 24px", fontSize: 14, cursor: "pointer", border: "none", background: "none",
              borderBottom: tab === t.value ? "2px solid #e87722" : "2px solid transparent",
              color: tab === t.value ? "#e87722" : "#666",
              fontWeight: tab === t.value ? 600 : 400,
              marginBottom: -2,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── ENVIAR ── */}
      <div className="cust-card" style={{ marginBottom: 24 }}>
        <div className="cust-section-header">Enviar Nova Notificação</div>

        <div className="cust-fields" style={{ paddingTop: 8 }}>
          <div className="cust-field">
            <label className="cust-label">Título *</label>
            <input className="cust-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título da notificação" />
          </div>
          <div className="cust-field">
            <label className="cust-label">URL (ao clicar)</label>
            <input className="cust-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." />
          </div>
          <div className="cust-field full">
            <label className="cust-label">Mensagem</label>
            <textarea className="cust-textarea" rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Corpo da notificação" />
          </div>

          <div className="cust-field full">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
              <label className="cust-label" style={{ margin: 0 }}>
                Selecionar {tab === "users" ? "Vendedores" : "Clientes"}
              </label>
              <span style={{ fontSize: 11, color: "#4caf50", display: "flex", alignItems: "center", gap: 3 }}>
                <NotificationsActiveIcon style={{ fontSize: 13 }} />{activeCount} ativos
              </span>
              <span style={{ fontSize: 11, color: "#999", display: "flex", alignItems: "center", gap: 3 }}>
                <NotificationsOffIcon style={{ fontSize: 13 }} />{totalList - activeCount} inativos
              </span>
              <span style={{ fontSize: 11, color: "#f4b400", display: "flex", alignItems: "center", gap: 3 }}>
                <PhoneAndroidIcon style={{ fontSize: 13 }} />{installedCount} instalados
              </span>
              <span style={{ fontSize: 11, color: "#2196f3", display: "flex", alignItems: "center", gap: 3 }}>
                <LanguageIcon style={{ fontSize: 13 }} />{browserCount} browser
              </span>
            </div>

            <SubFilterBar value={subFilter} onChange={setSubFilter} />

            {renderRecipientList()}
          </div>
        </div>

        <div style={{ padding: "0 16px 16px", display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SendIcon />}
            onClick={handleSend}
            disabled={sending || !title.trim() || selected.length === 0}
            size="small"
          >
            {sending ? "Enviando..." : `Enviar para ${selected.length} ${recipientLabel}`}
          </Button>
        </div>
      </div>

      {/* ── HISTÓRICO ── */}
      <div className="cust-card">
        <div className="cust-section-header">Histórico de Mensagens</div>
        <div style={{ padding: "8px 16px 16px" }}>
          <CustomGrid
            gridId="pushMsgList"
            rows={filteredMessages}
            columns={historyColumns}
            getRowId={(r) => r.id}
            tableHeight="500px"
          />
        </div>
      </div>
    </div>
  );
}
